import React, { useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Competence } from '../types'
import ProgressBar from './ProgressBar'
import { BackButton, Button, StrokedButton } from 'views/components/UI/Buttons'
import CobrandingLogo from 'views/components/UI/Logo/logo-cobranding'
import saveIcon from 'assets/images/save-icon.png'
import arrowDown from 'assets/images/arrow-down-icon.png'

interface QuestionViewProps {
  competence: Competence
  currentQuestionIndex: number
  answeredQuestions: Record<number, Record<string, number>> // questionIndex -> { evaluatedUserId -> score }
  totalCompetences: number
  completedCompetences: number
  companyLogo: string
  peopleToEvaluate: Array<{
    id: string
    name: string
    position?: string
    department?: string
    isSelfEvaluation?: boolean
  }>
  onAnswerQuestion: (questionIndex: number, evaluatedUserId: string, score: number) => void | Promise<void>
  onNext: () => void
  onBack: () => void
  onSaveAndContinueLater: () => void
  competenceSequentialNumber: number
  isLastCompetence: boolean
  isLoading: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0 60px 0;
  margin: 0 auto;
  max-width: 1400px;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
  width: 100%;
`

const HeaderContainer = styled.div`
  margin-bottom: 10px;
`

const CompetenceSection = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
`

const CompetenceNumber = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 5px 0;
  background-color: #2d749c;
  border-radius: 50px;
  padding: 8px 25px;
  display: inline-block;
`

const CompetenceDescription = styled.p`
  font-size: 18px;
  color: #1f2124;
  line-height: 1.5;
  margin: 0 0 30px 0;
`

const QuestionSection = styled.div`
  // margin-bottom: 32px;
`

const QuestionText = styled.p`
  font-size: 16px;
  color: #1f2124;
  font-weight: 600;
  margin: 0 0 10px 0;
`

const EachQuestionText = styled.p`
  font-size: 18px;
  color: #1f2124;
  font-weight: 700;
  margin: 0 0 24px 0;
`

const QuestionNumberBadge = styled.span`
  background-color: #2d749c;
  color: #ffffff;
  border-radius: 80px;
  padding: 10px;
  font-size: 18px;
  font-weight: 700;
  display: inline-block;
  margin-right: 8px;
`

const EvaluationTable = styled.div`
  background-color: #fafcff;
  border-radius: 8px;
  position: relative;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const TopQuestionSection = styled.div`
  position: sticky;
  top: 0;
  background-color: #fafcff;
  z-index: 5;
  border-radius: 8px 8px 0 0;
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 100px 100px 100px 100px;
  background: rgba(230, 171, 203, 0.15);
  padding: 12px 16px;
  font-weight: 600;
  color: #1f2124;
  font-size: 16px;
`

const HeaderItem = styled.span`
  font-weight: 600;
  color: #1f2124;
  font-size: 16px;
`

const ScoreHeaderItem = styled.span`
  font-weight: 600;
  color: #1f2124;
  font-size: 16px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`

const TableRow = styled.div<{ highlighted?: boolean }>`
  display: grid;
  grid-template-columns: 60px 1fr 100px 100px 100px 100px;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
  background: ${(props) => (props.highlighted ? '#f8f9fa' : 'white')};

  &:last-child {
    border-bottom: none;
  }
`

const EvaluatorNumber = styled.span`
  color: #1f2124;
  font-size: 14px;
  font-weight: 500;
`

const EvaluatorName = styled.span`
  color: #1f2124;
  font-size: 16px;
`

const EvaluatorType = styled.span`
  color: #2d749c;
  font-size: 16px;
  margin-left: 4px;
`

const ScoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ScoreOption = styled.button<{ selected: boolean; disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.selected ? '#24A0ED' : props.disabled ? '#ccc' : '#e0e0e0')};
  background: ${(props) => (props.disabled ? '#f5f5f5' : 'white')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) => (props.disabled ? (props.selected ? '#24A0ED' : '#ccc') : '#24a0ed')};
  }

  &:disabled {
    pointer-events: none;
  }

  &::after {
    content: '';
    width: ${(props) => (props.selected ? '16px' : '0')};
    height: ${(props) => (props.selected ? '16px' : '0')};
    border-radius: 50%;
    background: ${(props) => (props.disabled && props.selected ? '#999' : '#24a0ed')};
    transition: all 0.2s ease-in-out;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`

const RightButtonsGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const Icon = styled.img`
  width: 30px;
  height: 30px;
`

const ArrowIcon = styled.img`
  width: 20px;
  height: 20px;
`

const ScoreDescriptionList = styled.ul`
  margin-top: 6px;
  margin-bottom: 10px;
  padding-left: 20px;
  list-style-type: disc;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  grid-auto-flow: column;
  gap: 0 40px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-auto-flow: row;
    gap: 0;
  }
`

const ScoreDescriptionItem = styled.li`
  font-size: 16px;
  color: #1f2124;
  font-weight: 400;
  line-height: 1.8;
  padding-left: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    display: inline-block;
    min-width: 20px;
  }
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background: #1f2124;
  margin: 16px 0;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 8px;
`

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #24a0ed;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.p`
  margin-top: 16px;
  font-size: 16px;
  color: #1f2124;
  font-weight: 500;
`

const MiniSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #24a0ed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 8px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const QuestionView: React.FC<QuestionViewProps> = ({
  competence,
  currentQuestionIndex,
  answeredQuestions,
  onAnswerQuestion,
  onNext,
  onBack,
  onSaveAndContinueLater,
  totalCompetences,
  completedCompetences,
  companyLogo,
  peopleToEvaluate,
  competenceSequentialNumber,
  isLastCompetence,
  isLoading,
}) => {
  const currentQuestion = competence.questions[currentQuestionIndex]
  const prevCompetenceRef = useRef(competenceSequentialNumber)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentQuestionAnswers = useMemo(() => {
    const safeAnsweredQuestions = answeredQuestions && typeof answeredQuestions === 'object' ? answeredQuestions : {}
    return safeAnsweredQuestions[currentQuestionIndex] &&
      typeof safeAnsweredQuestions[currentQuestionIndex] === 'object'
      ? safeAnsweredQuestions[currentQuestionIndex]
      : {}
  }, [answeredQuestions, currentQuestionIndex])

  // Scroll al cambiar de competencia
  useEffect(() => {
    if (prevCompetenceRef.current !== competenceSequentialNumber) {
      // Hacer scroll en todos los posibles contenedores
      const scrollToTop = () => {
        // Intentar con todos los posibles elementos que puedan tener scroll
        window.scrollTo({ top: 0, behavior: 'smooth' })
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0

        // Buscar todos los elementos con scroll y hacer scroll en ellos
        const allElements = document.querySelectorAll('*')
        allElements.forEach((element: any) => {
          if (element.scrollTop > 0) {
            element.scrollTo({ top: 0, behavior: 'smooth' })
          }
        })
      }

      // Ejecutar inmediatamente y con delay
      scrollToTop()
      setTimeout(scrollToTop, 50)
      setTimeout(scrollToTop, 100)

      prevCompetenceRef.current = competenceSequentialNumber
    }
  }, [competenceSequentialNumber])

  const isLastQuestion = currentQuestionIndex === competence.questions.length - 1

  // Determinar el texto del botón
  const getButtonText = () => {
    if (!isLastQuestion) {
      return 'Siguiente ítem'
    }

    if (isLastCompetence) {
      return 'Terminar'
    }

    return 'Siguiente competencia'
  }

  const handleScoreSelect = (evaluatedUserId: string, score: number) => {
    // No permitir cambios si está cargando
    if (isLoading) {
      return
    }

    if (
      currentQuestionIndex === undefined ||
      currentQuestionIndex === null ||
      typeof currentQuestionIndex !== 'number'
    ) {
      console.error('Invalid currentQuestionIndex in handleScoreSelect:', currentQuestionIndex)
      return
    }

    if (!evaluatedUserId || typeof evaluatedUserId !== 'string') {
      console.error('Invalid evaluatedUserId in handleScoreSelect:', evaluatedUserId)
      return
    }

    if (score === undefined || score === null || typeof score !== 'number') {
      console.error('Invalid score in handleScoreSelect:', score)
      return
    }

    // Guardar inmediatamente como en todas las otras preguntas
    onAnswerQuestion(currentQuestionIndex, evaluatedUserId, score)
  }

  const handleNext = async () => {
    onNext()
  }

  const handleSaveAndContinueLater = async () => {
    onSaveAndContinueLater()
  }

  const handleBack = () => {
    // Si estamos en la última pregunta y hay respuestas temporales, no las confirmamos
    // (se pierden al salir de la pregunta)
    onBack()
  }

  // Check if all people have been evaluated for this question
  const canProceed = useMemo(() => {
    return (
      Array.isArray(peopleToEvaluate) &&
      peopleToEvaluate.every((person) => person && person.id && currentQuestionAnswers[person.id] !== undefined)
    )
  }, [currentQuestionAnswers, peopleToEvaluate])

  const formatEvaluatorNumber = (num: number): string => {
    return num < 10 ? `0${num}.` : `${num}.`
  }

  return (
    <Container ref={containerRef}>
      <HeaderContainer>
        <Header>{companyLogo && <CobrandingLogo logoUrl={companyLogo} hideMobile />}</Header>
        <ProgressBar
          title="Evaluación 360°"
          totalCompetences={totalCompetences}
          completedCompetences={completedCompetences}
        />
      </HeaderContainer>

      <CompetenceSection>
        <CompetenceNumber>
          {competenceSequentialNumber}. {competence.name}
        </CompetenceNumber>
        <CompetenceDescription>{competence.description}</CompetenceDescription>
        <QuestionText
          style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          Asigna a cada uno de los evaluados el puntaje que represente su desempeño en cada comportamiento.
          <ArrowIcon src={arrowDown} alt="Flecha hacia abajo" />
        </QuestionText>
        <ScoreDescriptionList>
          <ScoreDescriptionItem>
            <strong>1.</strong> Rara vez demuestra el comportamiento
          </ScoreDescriptionItem>
          <ScoreDescriptionItem>
            <strong>2.</strong> Lo demuestra de manera ocasional o inconsistente
          </ScoreDescriptionItem>
          <ScoreDescriptionItem>
            <strong>3.</strong> Lo demuestra con frecuencia y de forma adecuada
          </ScoreDescriptionItem>
          <ScoreDescriptionItem>
            <strong>4.</strong> Lo demuestra de manera consistente y ejemplar
          </ScoreDescriptionItem>
        </ScoreDescriptionList>
        <Line />
      </CompetenceSection>

      <QuestionSection>
        <EvaluationTable>
          <TopQuestionSection>
            <EachQuestionText>
              <QuestionNumberBadge>
                {competenceSequentialNumber}.{currentQuestionIndex + 1}
              </QuestionNumberBadge>
              {currentQuestion}
            </EachQuestionText>

            <TableHeader>
              <HeaderItem>#</HeaderItem>
              <HeaderItem>Nombre del evaluado</HeaderItem>
              <ScoreHeaderItem>1</ScoreHeaderItem>
              <ScoreHeaderItem>2</ScoreHeaderItem>
              <ScoreHeaderItem>3</ScoreHeaderItem>
              <ScoreHeaderItem>4</ScoreHeaderItem>
            </TableHeader>
          </TopQuestionSection>

          {peopleToEvaluate.map((person, index) => {
            const personAnswer = currentQuestionAnswers[person.id]

            return (
              <TableRow key={person.id} highlighted={false}>
                <EvaluatorNumber>{formatEvaluatorNumber(index + 1)}</EvaluatorNumber>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div>
                    <EvaluatorName>{person.name}</EvaluatorName>
                    {person.isSelfEvaluation ? (
                      <EvaluatorType>(Autoevaluación)</EvaluatorType>
                    ) : (
                      <EvaluatorType>(Tu rol: {person.position})</EvaluatorType>
                    )}
                  </div>
                  {isLoading && <MiniSpinner />}
                </div>
                {[1, 2, 3, 4].map((score) => (
                  <ScoreContainer key={score}>
                    <ScoreOption
                      selected={personAnswer === score}
                      disabled={isLoading}
                      onClick={() => handleScoreSelect(person.id, score)}
                    />
                  </ScoreContainer>
                ))}
              </TableRow>
            )
          })}

          {isLoading && (
            <LoadingOverlay>
              <Spinner />
              <LoadingText>Guardando respuestas...</LoadingText>
            </LoadingOverlay>
          )}
        </EvaluationTable>
      </QuestionSection>

      <ButtonsContainer>
        <BackButton
          onClick={isLoading ? undefined : handleBack}
          style={{
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
        />

        <RightButtonsGroup>
          <StrokedButton
            style={{ margin: '0', display: 'flex', alignItems: 'center', gap: '12px' }}
            onClick={handleSaveAndContinueLater}
            disabled={isLoading}
          >
            <Icon src={saveIcon} alt="Guardar" />
            Guardar y continuar después
          </StrokedButton>

          <Button disabled={!canProceed || isLoading} onClick={handleNext}>
            {getButtonText()}
          </Button>
        </RightButtonsGroup>
      </ButtonsContainer>
    </Container>
  )
}

export default QuestionView
